import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useChainContext } from '../contexts/ChainContext';
import chainConfig, { getChainConfig } from '../config/chainConfig';
import ConfirmModal from './ConfirmModal';
import ThemeToggle from './ThemeToggle';
import { isLocalhost } from '../utils/environment';
import { Menu, ChevronDown, User, X, Link2 } from 'lucide-react';

// Las librerías de login pesan mucho: solo se descargan si hay una sesión guardada
// que recuperar o cuando el usuario pulsa "Log in".
// Telos: Anchor vía UAL. XPR: SDK oficial (WebAuth y Anchor).
const WalletConnector = lazy(() => import('./WalletConnector'));
const ProtonConnector = lazy(() => import('./ProtonConnector'));
const savedSessionKey = (isXpr) => (isXpr ? 'proton-storage-user-auth' : 'UALLoggedInAuthType');
const hasSavedSession = (isXpr) => {
  try { return !!localStorage.getItem(savedSessionKey(isXpr)); } catch { return false; }
};
const loginBtnClass = 'btn-primary h-9 px-4 text-sm font-semibold whitespace-nowrap disabled:opacity-50';

const WalletSlot = () => {
  const { currentChain } = useChainContext();
  const isXpr = getChainConfig(currentChain).chainFamily === 'XPR';
  const Connector = isXpr ? ProtonConnector : WalletConnector;
  const [load, setLoad] = useState(() => hasSavedSession(isXpr));
  const [openOnLoad, setOpenOnLoad] = useState(false);
  if (!load) {
    return (
      <button onClick={() => { setOpenOnLoad(true); setLoad(true); }} className={loginBtnClass}>
        Log in
      </button>
    );
  }
  return (
    <Suspense fallback={<button disabled className={loginBtnClass}>{openOnLoad ? 'Logging in...' : 'Log in'}</button>}>
      <Connector openOnLoad={openOnLoad} />
    </Suspense>
  );
};

const chainFamilies = [
  ['telos-mainnet', 'telos-testnet'],
  ['xpr-mainnet', 'xpr-testnet']
];

// Menú principal (antes vivía en el Sidebar)
const getNavItems = (currentChain) => {
  const chain = getChainConfig(currentChain);
  return [
    { name: 'Home', path: '/', end: true },
    { name: 'Blocks', path: '/blocks' },
    { name: 'Producers', path: '/producers' },
    { name: 'Market', path: '/market' },
    { name: 'Chain', path: '/chain' },
    { name: 'Multisig', path: '/multisig' },
    ...(currentChain === 'telos-mainnet' ? [{
      name: 'Top Holders',
      prefix: '/top-holders',
      subItems: [
        { name: 'Total Amount', path: '/top-holders/total' },
        { name: 'RAM', path: '/top-holders/ram' },
        { name: 'REX', path: '/top-holders/rex' }
      ]
    }] : []),
    {
      name: 'Wallet',
      prefix: '/wallet',
      subItems: [
        { name: 'Transfer Tokens', path: '/wallet/transfer' },
        // XPR no usa el sistema de recursos CPU/NET/RAM
        ...(chain.chainFamily !== 'XPR' ? [
          { name: 'Resources (CPU/NET/RAM)', path: '/wallet/cpu-net-ram' }
        ] : []),
        { name: 'Permissions', path: '/wallet/keys' },
        { name: 'Vote', path: '/wallet/vote' },
        ...(chain.chainFamily !== 'XPR' ? [{ name: 'Create Account', path: '/wallet/create-account' }] : [])
      ]
    }
  ];
};

const navLinkClass = (active) =>
  `text-sm transition-colors ${active ? 'text-indigo-700 font-semibold' : 'text-gray-700 hover:text-indigo-700'}`;

// Desplegable de escritorio (Wallet, Top Holders)
const NavDropdown = ({ item, pathname }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = pathname.startsWith(item.prefix);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1 ${navLinkClass(active)}`}
      >
        {item.name}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-3 w-64 bg-surface border border-gray-200 rounded-lg shadow-lg py-1 z-40">
          {item.subItems.map((sub) => (
            <Link
              key={sub.path}
              to={sub.path}
              className={`block px-4 py-2 text-sm ${pathname === sub.path ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-700'}`}
            >
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentChain, setCurrentChain, user, isAuthenticated, logout } = useChainContext();
  const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChainChange, setPendingChainChange] = useState(null);
  const dropdownRef = useRef(null);

  const navItems = getNavItems(currentChain);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsNetworkDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cerrar el menú móvil al navegar
  useEffect(() => { setIsMobileMenuOpen(false); }, [pathname]);

  const currentNetworkLogo = chainConfig[currentChain]?.logo || null;
  const currentNetworkName = chainConfig[currentChain]?.name || 'Select Network';

  const handleChainChange = (chainId) => {
    if (isAuthenticated) {
      setPendingChainChange(chainId);
      setShowConfirmModal(true);
    } else {
      executeChainChange(chainId);
    }
  };

  const executeChainChange = async (chainId) => {
    setIsNetworkDropdownOpen(false);
    setIsMobileMenuOpen(false);

    if (isLocalhost()) {
      // En desarrollo: logout, cambiar configuración y redirigir a home
      if (isAuthenticated) {
        await logout();
      }
      setCurrentChain(chainId);
      navigate('/');
    } else {
      // En producción: logout y redirigir al subdominio
      if (isAuthenticated) {
        await logout();
      }
      const subdomain = chainConfig[chainId].subdomain;
      window.location.href = `https://${subdomain}.chainscout.io/`;
    }
  };

  const handleConfirmChainChange = async () => {
    setShowConfirmModal(false);
    if (pendingChainChange) {
      await executeChainChange(pendingChainChange);
      setPendingChainChange(null);
    }
  };

  const handleCancelChainChange = () => {
    setShowConfirmModal(false);
    setPendingChainChange(null);
  };

  return (
    <>
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleCancelChainChange}
        onConfirm={handleConfirmChainChange}
        title="Network Change Warning"
        message="Changing the blockchain network will disconnect your wallet. You'll be redirected to the homepage. Do you want to continue?"
      />

      <header className="bg-surface border-b border-gray-200 relative z-30">
        <div className="page-container h-16 flex items-center gap-4 lg:gap-8">
          <Link to="/" className="flex items-center gap-2.5 text-gray-900 font-bold text-lg hover:no-underline shrink-0">
            <Link2 size={26} className="text-indigo-700" strokeWidth={2} aria-hidden="true" />
            ChainScout
          </Link>

          {/* Menú de escritorio */}
          <nav aria-label="Main" className="hidden lg:flex items-center gap-6 flex-1">
            {navItems.map((item) =>
              item.subItems ? (
                <NavDropdown key={item.name} item={item} pathname={pathname} />
              ) : (
                <NavLink key={item.path} to={item.path} end={item.end} className={({ isActive }) => navLinkClass(isActive)}>
                  {item.name}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 ml-auto lg:ml-0">
            <ThemeToggle />
            <NetworkSelector
              currentNetworkLogo={currentNetworkLogo}
              currentNetworkName={currentNetworkName}
              isNetworkDropdownOpen={isNetworkDropdownOpen}
              setIsNetworkDropdownOpen={setIsNetworkDropdownOpen}
              dropdownRef={dropdownRef}
              handleChainChange={handleChainChange}
              currentChain={currentChain}
            />
            {isAuthenticated && user && (
              <Link
                to={`/account-detail/${user.name}`}
                className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-900 hover:bg-gray-50 hover:no-underline"
                title="My account"
              >
                <User size={16} className="text-gray-500" />
                {user.name}
              </Link>
            )}
            {/* Montado una sola vez: contiene el UALProvider */}
            <WalletSlot key={getChainConfig(currentChain).chainFamily} />
            <button
              type="button"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Menú móvil */}
        {isMobileMenuOpen && (
          <nav aria-label="Main" className="lg:hidden border-t border-gray-200 bg-surface">
            <div className="page-container py-3 space-y-1">
              {isAuthenticated && user && (
                <Link to={`/account-detail/${user.name}`} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50 sm:hidden">
                  <User size={16} className="text-gray-500" />
                  {user.name}
                </Link>
              )}
              {navItems.map((item) =>
                item.subItems ? (
                  <div key={item.name} className="pt-2">
                    <div className="px-3 pb-1 stat-label">{item.name}</div>
                    {item.subItems.map((sub) => (
                      <NavLink
                        key={sub.path}
                        to={sub.path}
                        className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm ${isActive ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {sub.name}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm ${isActive ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    {item.name}
                  </NavLink>
                )
              )}
            </div>
          </nav>
        )}
      </header>
    </>
  );
}

// Selector de red: botón blanco con borde (logo + nombre) y desplegable
const NetworkSelector = ({
  currentNetworkLogo,
  currentNetworkName,
  isNetworkDropdownOpen,
  setIsNetworkDropdownOpen,
  dropdownRef,
  handleChainChange,
  currentChain
}) => (
  <div className="relative" ref={dropdownRef}>
    <button
      type="button"
      className="flex items-center gap-2 h-9 px-3 text-sm text-gray-900 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      onClick={() => setIsNetworkDropdownOpen(!isNetworkDropdownOpen)}
      aria-haspopup="true"
      aria-expanded={isNetworkDropdownOpen}
      aria-label={`Network: ${currentNetworkName}`}
    >
      {currentNetworkLogo
        ? <img src={currentNetworkLogo} alt="" className="w-4 h-4" />
        : <span aria-hidden="true" className="w-2 h-2 rounded-full bg-green-600" />}
      <span className="hidden sm:inline">{currentNetworkName}</span>
      <ChevronDown size={14} />
    </button>

    {isNetworkDropdownOpen && (
      <div className="absolute right-0 mt-2 w-52 rounded-lg shadow-lg bg-surface border border-gray-200 z-40">
        <div className="py-1" role="menu" aria-orientation="vertical">
          {chainFamilies.map((family, familyIndex) => (
            <React.Fragment key={familyIndex}>
              {familyIndex > 0 && <hr className="my-1 border-gray-100" />}
              {family.map((chainId) => (
                <button
                  type="button"
                  key={chainId}
                  className={`${
                    chainId === currentChain ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                  } px-4 py-2 text-sm hover:bg-gray-50 w-full text-left flex items-center gap-2`}
                  onClick={() => handleChainChange(chainId)}
                  role="menuitem"
                >
                  <img src={chainConfig[chainId].logo} alt="" className="w-5 h-5" />
                  <span>{chainConfig[chainId].name}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default Header;
